import {Positions} from '../enums';
import {Baseline, PlayerSession, Player, Team} from '../interfaces';
import {getMean} from './mathService';

export const getPlayerProfileName = ({
  team,
  profileId,
}: {
  team: Team;
  profileId: number;
}) => {
  const matchingPlayers = (team?.players ?? []).filter(
    (player: Player) => player.profile?.id === profileId,
  );

  // if current user if mathcing players is empty
  if (matchingPlayers.length === 0) {
    return '';
  }

  return `${matchingPlayers[0]?.profile?.firstName} ${matchingPlayers[0]?.profile?.lastName}`;
};

export const getPlayerProfile = ({
  team,
  profileId,
}: {
  team: Team;
  profileId: number;
}) => {
  const matchingPlayers = (team?.players ?? []).filter(
    (player: Player) => player.profile?.id === profileId,
  );

  // if current user if mathcing players is empty
  if (matchingPlayers.length === 0) {
    return null;
  }

  return matchingPlayers[0]?.profile;
};

export const getTeamSessionEnergyRatio = (playerSessions: PlayerSession[]) => {
  const validPlayerSessions = (playerSessions ?? []).filter(
    ps => (ps.activeTime ?? 0) > 0,
  );
  if (!validPlayerSessions.length) {
    return null;
  }
  return getMean(validPlayerSessions.map(ps => ps.energyRatio as number));
};

export const getTeamBaselineAverageEnergyRatio = (baselines: Baseline[]) => {
  const validBaselines = (baselines ?? []).filter(b => b && b.energyRatio);
  if (!validBaselines.length) {
    return null;
  }
  const totalBaselines = validBaselines.reduce((acc, curr) => {
    return acc + (curr?.energyRatio ?? 0);
  }, 0);
  return totalBaselines / validBaselines.length;
};

// Roster order, forwards first. Used both to sort players and to rank the
// position column, so it is the single source of truth for position ordering.
export const positions: string[] = [
  Positions.center,
  Positions.leftwing,
  Positions.rightwing,
  Positions.defense,
  Positions.goalie,
];

export const forwardPositions: string[] = [
  Positions.center,
  Positions.leftwing,
  Positions.rightwing,
];

const positionAbbreviations: Record<string, string> = {
  [Positions.center]: 'C',
  [Positions.leftwing]: 'LW',
  [Positions.rightwing]: 'RW',
  [Positions.defense]: 'D',
  [Positions.goalie]: 'G',
};

export const getPositionAbbreviation = (position?: string) => {
  if (!position) {
    return null;
  }
  return positionAbbreviations[position] ?? position.toUpperCase();
};

export const getPositionRank = (position?: string) => {
  const rank = positions.indexOf(position ?? '');
  // Unknown/missing positions rank after every known one.
  return rank === -1 ? positions.length : rank;
};

export const playersSort = ({players}: {players: Player[]}) => {
  return [...players].sort((a, b) => {
    // first sort by position, then by last name
    const positionA = a.profile?.position;
    const positionB = b.profile?.position;
    if (positionA && positionB && positionA !== positionB) {
      return getPositionRank(positionA) - getPositionRank(positionB);
    }
    return (a.profile?.lastName ?? '').localeCompare(b.profile?.lastName ?? '');
  });
};
